import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { ApiKey } from '@app/api/api-keys/types';

import { formatDate, formatFullDate } from '../utils/dates';
import { DeleteButton } from './DeleteButton';

interface KeysTableProps {
  keys: ApiKey[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  onCancelDelete: (id: string) => void;
  pendingDeletions: Set<string>;
  deletingKeys?: Set<string>;
  className?: string;
}

export function KeysTable({
  keys,
  isLoading,
  onDelete,
  onCancelDelete,
  pendingDeletions,
  deletingKeys = new Set(),
  className = '',
}: KeysTableProps) {
  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Key ID
              </th>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Created
              </th>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Expires
              </th>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[180px]"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-2 py-16">
                  <div className="flex justify-center">
                    <LoadingSpinner size="lg" />
                  </div>
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-16 text-center text-gray-500 dark:text-gray-400">
                  No API keys to display
                </td>
              </tr>
            ) : (
              keys.map((key) => (
                <tr key={key.id}>
                  <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {key.name}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm font-mono text-gray-500 dark:text-gray-400">
                    {key.id}
                  </td>
                  <td
                    className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                    title={formatFullDate(key.created_at)}
                  >
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {key.expires_at ? (
                      <span title={formatFullDate(key.expires_at)}>
                        {formatDate(key.expires_at)}
                      </span>
                    ) : (
                      'Never'
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        isKeyExpired(key.expires_at)
                          ? 'bg-red-100 text-red-800'
                          : key.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {isKeyExpired(key.expires_at)
                        ? 'Expired'
                        : key.is_active
                          ? 'Active'
                          : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm">
                    <DeleteButton
                      onDelete={() => onDelete(key.id)}
                      onCancel={() => onCancelDelete(key.id)}
                      isPending={pendingDeletions.has(key.id)}
                      isDeleting={deletingKeys.has(key.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function isKeyExpired(expiresAt: Date | null | undefined): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < new Date().getTime();
}
